/**
 * @file Header.js
 * @module src/js/components/Header
 * @description
 * **Header component for the EDALab frontend.**
 * - Displays different header items based on user authentication status.
 * - Highlights the current section on screen.
 * - Provides smooth scrolling to sections on the homepage.
 * - Includes a menu toggle for mobile navigation.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: Remove states homepageHeaderItems and headerItems. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Disable logout button when logging out. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useHandleLogout hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useMemo, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from '../contexts/UserProvider';
import useHandleLogout from '../hooks/useHandleLogout';
import getDevice from '../functions/getDevice';
import { toKebabCase } from './UtilityComponents';

export default function Header({
    handleMouseEnterMenu,
    handleClickMenu,
    isHeaderVisible,
    headerBgOpacity
}) {
    const { pathname } = useLocation();
    const { uid, isLoggingOut } = useUser();
    const handleLogout = useHandleLogout();
    const [whichSectionOnScreen, setWhichSectionOnScreen] = useState("");
    const [isHoveringHeaderItem, setIsHoveringHeaderItem] = useState(-1);
    const { isMobile } = getDevice();

    const homepageHeaderItems = useMemo(() => {
        if (uid === "Guest") {
            if (pathname === "/") return ["News", "Advisor", "Research", "Contact Us", "Log In 🔑"];
            if (pathname === "/login") return [];
            return ["Log In 🔑"];
        } else {
            return ["Profile", "Contact", "Archives", "Lab Data", "Paper Review", "New Member", "Log Out 🔐"];
        }
    }, [uid, pathname]);

    // ...homepageHeaderItems is null if homepageHeaderItems is [], .filter(Boolean) removes null values
    const headerItems = useMemo(() => ["EDA Lab", ...homepageHeaderItems].filter(Boolean), [homepageHeaderItems]);

    const handleClickHeader = useCallback((e) => {
        if (pathname === "/") {
            const id = toKebabCase(e.target.innerText);
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [pathname]);

    useEffect(() => {
        if (pathname === "/" && uid === "Guest") {
            const determineWhichSectionOnScreen = () => {
                const bannerHeight = document.getElementById('banner')?.offsetHeight || 0;
                const sectionOnScreen = homepageHeaderItems.find(item => {
                    const element = document.getElementById(toKebabCase(item));
                    return element && (window.scrollY < element.offsetTop + element.offsetHeight - bannerHeight);
                });
                setWhichSectionOnScreen(sectionOnScreen);
            };
            window.addEventListener('scroll', determineWhichSectionOnScreen);
            return () => window.removeEventListener('scroll', determineWhichSectionOnScreen);
        } else if (pathname !== "/") {
            const sectionOnScreen = homepageHeaderItems.find(item =>
                !!document.getElementById(toKebabCase(item))
            );
            setWhichSectionOnScreen(sectionOnScreen);
        } else {
            setWhichSectionOnScreen("");
        }
    }, [uid, pathname, homepageHeaderItems]);

    return (
        <header
            id="header"
            className={isHeaderVisible ? "" : "alt"}
            style={{backgroundColor: `rgba(46,56,66,${headerBgOpacity})`}}
        >
            <h1><ul style={{
                    listStyle: 'none',
                    overflowX: 'auto',                  // Enable horizontal scrolling
                    overflowY: 'hidden',                // Disable vertical scrolling
                    whiteSpace: 'nowrap',               // Prevent items from wrapping
                    width: isMobile ? '80vw' : '90vw',  // Set width to fit the container
                    padding: 0,                         // Remove default padding
                    margin: 0,                          // Remove default margin
                    display: 'flex',                    // Display items in a single row
                    flexWrap: 'nowrap',                 // Prevent wrapping into new rows
                    scrollbarWidth: 'thin',             // For modern browsers
                    WebkitOverflowScrolling: 'touch'    // Smooth scrolling on mobile
                }}
            >
                {headerItems.map((item, i) => (
                    <li key={i}
                        style={{
                            display: 'inline-block', // Keep items in a line
                            float: 'left',
                            textAlign: 'center',
                            paddingLeft: uid === "Guest" ? '1.2em' : '0.8em',
                            paddingRight: uid === "Guest" ? '1.2em' : '0.8em',
                            cursor: 'pointer',
                            color: whichSectionOnScreen === item ? '#fffbc0ff' : 'white',
                            transform: whichSectionOnScreen === item ? 'translateY(-1px) scale(1.02)' : 'none',   // lift + micro-scale
                            textShadow: whichSectionOnScreen === item ? '0 2px 14px rgba(255,247,130,.5)' : 'none', // soft glow
                            backgroundColor: isHoveringHeaderItem === i ? '#455461' : 'transparent',
                            transition: 'all 0.2s ease-in-out'
                        }}
                        onMouseEnter={() => setIsHoveringHeaderItem(i)}
                        onMouseLeave={() => setIsHoveringHeaderItem(-1)}
                    >{
                        item === "EDA Lab" ? <Link to="/">{item}</Link> :
                        item === "Profile" ? <Link to="/member-zone/profile">{item}</Link> :
                        item === "Contact" ? <Link to="/member-zone/contact">{item}</Link> :
                        item === "Archives" ? <Link to="/member-zone/archives">{item}</Link> :
                        item === "Lab Data" ? <Link to="/member-zone/lab-data">{item}</Link> :
                        item === "Paper Review" ? <Link to="/member-zone/paper-review">{item}</Link> :
                        item === "New Member" ? <Link to="/member-zone/new-member">{item}</Link> :
                        item === "Log In 🔑" ? <Link to="/login" onClick={() => setIsHoveringHeaderItem(-1)}>{item}</Link> :
                        item === "Log Out 🔐" ? (
                            <div 
                                style={{
                                    cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                                    opacity: isLoggingOut ? 0.4 : 1,
                                }}
                                onClick={async () => {
                                    if (isLoggingOut) return;
                                    await handleLogout();
                                    setIsHoveringHeaderItem(-1);
                                }}
                            >{item}</div>
                        ) : <div onClick={handleClickHeader}>{item}</div>
                    }</li>
                ))}
            </ul></h1>
            <nav id="nav">
                <ul>
                    <li className="special">
                        <a
                            href="#menu"
                            className="menuToggle"
                            onMouseEnter={handleMouseEnterMenu}
                            onClick={handleClickMenu}
                        >
                            <span>Menu</span>
                        </a>
                    </li>
                </ul>
            </nav>
        </header>
    );
}
